
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

type HistoryItem = Tables<"history_items">;
type HistoryCategory = Tables<"history_categories">;

interface HistoryItemFormProps {
  item?: HistoryItem | null;
  categories: HistoryCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const HistoryItemForm = ({ item, categories, onClose, onSuccess }: HistoryItemFormProps) => {
  const [formData, setFormData] = useState({
    name: item?.name || "",
    category_id: item?.category_id || "",
    period: item?.period || "",
    achievement: item?.achievement || "",
    story: item?.story || "",
    fun_facts: item?.fun_facts?.join("\n") || "",
    image_url: item?.image_url || "",
    display_order: item?.display_order || 0,
    is_active: item?.is_active ?? true,
    seo_title: item?.seo_title || "",
    meta_description: item?.meta_description || "",
    slug: item?.slug || "",
    keywords: item?.keywords?.join(", ") || "",
    image_alt_text: item?.image_alt_text || ""
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const funFactsArray = formData.fun_facts
        .split("\n")
        .filter(fact => fact.trim())
        .map(fact => fact.trim());

      // Auto-generate slug if empty
      const autoSlug = formData.slug || formData.name.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const keywordsArray = formData.keywords
        .split(",")
        .filter(keyword => keyword.trim())
        .map(keyword => keyword.trim());

      const submitData = {
        ...formData,
        fun_facts: funFactsArray,
        slug: autoSlug,
        keywords: keywordsArray.length > 0 ? keywordsArray : null,
        seo_title: formData.seo_title || null,
        meta_description: formData.meta_description || null,
        image_alt_text: formData.image_alt_text || null
      };

      if (item) {
        const { error } = await supabase
          .from("history_items")
          .update(submitData)
          .eq("id", item.id);

        if (error) throw error;
        toast({ title: "Item updated successfully" });
      } else {
        const { error } = await supabase
          .from("history_items")
          .insert(submitData);

        if (error) throw error;
        toast({ title: "Item created successfully" });
      }

      onSuccess();
    } catch (error) {
      console.error("Error saving item:", error);
      toast({
        title: "Error",
        description: "Failed to save item",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {item ? "Edit History Item" : "Add History Item"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="period">Period</Label>
            <Input
              id="period"
              value={formData.period}
              onChange={(e) => setFormData({ ...formData, period: e.target.value })}
              placeholder="e.g., 1869-1948"
            />
          </div>

          <div>
            <Label htmlFor="achievement">Achievement</Label>
            <Input
              id="achievement"
              value={formData.achievement}
              onChange={(e) => setFormData({ ...formData, achievement: e.target.value })}
              placeholder="e.g., Father of the Nation"
            />
          </div>

          <div>
            <Label htmlFor="story">Story</Label>
            <RichTextEditor
              value={formData.story}
              onChange={(value) => setFormData({ ...formData, story: value })}
              placeholder="Enter the historical story"
              height="200px"
            />
          </div>

          <div>
            <Label htmlFor="fun_facts">Fun Facts (one per line)</Label>
            <Textarea
              id="fun_facts"
              value={formData.fun_facts}
              onChange={(e) => setFormData({ ...formData, fun_facts: e.target.value })}
              rows={4}
              placeholder="🏛️ Built over 84,000 stupas across India&#10;📜 His edicts are found on rocks and pillars"
            />
          </div>

          <div>
            <Label htmlFor="image_url">Image URL</Label>
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          {/* SEO Fields Section */}
          <div className="border-t pt-4 space-y-4">
            <h3 className="text-lg font-semibold text-muted-foreground">SEO Settings (Optional)</h3>
            
            <div>
              <Label htmlFor="seo_title">SEO Title</Label>
              <Input
                id="seo_title"
                value={formData.seo_title}
                onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                placeholder="Custom SEO title for search engines"
                maxLength={60}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.seo_title.length}/60 characters
              </p>
            </div>

            <div>
              <Label htmlFor="meta_description">Meta Description</Label>
              <Textarea
                id="meta_description"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                placeholder="Brief description for search engines"
                rows={2}
                maxLength={160}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {formData.meta_description.length}/160 characters
              </p>
            </div>

            <div>
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="Auto-generated from name if empty"
              />
              <p className="text-xs text-muted-foreground mt-1">
                URL: /history/{formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
              </p>
            </div>

            <div>
              <Label htmlFor="keywords">Keywords / Tags</Label>
              <Input
                id="keywords"
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="history, ancient, civilization (comma-separated)"
              />
            </div>

            <div>
              <Label htmlFor="image_alt_text">Image Alt Text</Label>
              <Input
                id="image_alt_text"
                value={formData.image_alt_text}
                onChange={(e) => setFormData({ ...formData, image_alt_text: e.target.value })}
                placeholder="Descriptive text for the image"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="display_order">Display Order</Label>
            <Input
              id="display_order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryItemForm;
